import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Container,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useSharedLink from "../../SharedLink";
import axios from "axios";
import { xorDecrypt } from "../../encrypt";
import axiosInstance from "../../api/axiosInstance";

const InvoiceAdmin = () => {
  const sharedLink = useSharedLink();
  const [invoices, setInvoices] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false); // New state for checking admin role
  const [snackbarOpen, setSnackbarOpen] = useState(false); // State for Snackbar

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const response = await axiosInstance.post(
          `${sharedLink}/api/abanotes/getallsubscribers`,
          {},
          {
            withCredentials: true,
          }
        );
        if (response.status === 200) {
          const emails = response.data.map((subscriber) => subscriber.email);
          setSubscribers(emails);
        }
      } catch (error) {
        console.error("Failed to fetch subscribers:", error);
      }
    };

    fetchSubscribers();
  }, [sharedLink]);

  const fetchInvoices = async (email) => {
    try {
      const response = await axiosInstance.get(`${sharedLink}/invoice`, {
        params: {
          email: email,
        },
        withCredentials: true,
      });
      if (response.status === 200) {
        if (response.data.length === 0) {
          setSnackbarOpen(true); // Open Snackbar if response is empty
          setInvoices([]); // Clear invoices state
        } else {
          // Ensure each invoice has a unique id
          const invoicesWithId = response.data.map((invoice, index) => ({
            id: invoice.invoiceId || index, // Use invoiceId if available, otherwise fallback to index
            ...invoice,
          }));
          //console.log("Invoices fetched: ", invoicesWithId);
          setInvoices(invoicesWithId);
        }
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  };

  useEffect(() => {
    const userRole =xorDecrypt(localStorage.getItem("userRole")); // Get the user role from localStorage

    if (userRole === "admin") {
      setIsAdmin(true); // Set admin state to true
    } else {
      console.error("User is not an admin");
    }
  }, []);

  const handleDownload = (invoiceId) => {
    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", `${sharedLink}/download-invoice`);
    form.style.display = "none";

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "invoiceId";
    input.value = invoiceId;

    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();

    document.body.removeChild(form);
  };

  const columns = [
    { field: "dateOfPurchase", headerName: "Date of Purchase", width: 250 },
    { field: "transactionId", headerName: "Transaction ID", width: 250 },
    { field: "invoiceId", headerName: "Invoice ID", width: 150 },
    {
      field: "download",
      headerName: "Download",
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => handleDownload(params.row.invoiceId)}>
          <DownloadIcon style={{ color: "black" }} />
        </IconButton>
      ),
    },
  ];

  const theme = createTheme({
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            color: "black",
            border: "1px solid #e9a132", // Entire grid border
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#e9a132",
              color: "white",
              borderBottom: "2px solid #e9a132", // Header border
            },
            "& .MuiDataGrid-cell": {
              color: "black",
              borderBottom: "1px solid #e9a132", // Cell border
            },
            "& .MuiDataGrid-iconSeparator": {
              color: "black", // three dots
            },
            "& .MuiDataGrid-sortIcon": {
              color: "black", // sorting arrows
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "black", // menu button
            },
            "& .MuiSvgIcon-root": {
              color: "black", // all icons
            },
            "& .MuiDataGrid-footerContainer": {
              color: "black", // footer text
              borderTop: "2px solid #e9a132", // Footer border
            },
            "& .MuiTablePagination-root": {
              color: "black", // pagination text
            },
            "& .MuiTablePagination-actions": {
              color: "black", // pagination arrows
            },
            "& .MuiInputBase-root": {
              color: "black", // input text (e.g., rows per page)
            },
            "& .MuiSelect-icon": {
              color: "black", // dropdown arrow
            },
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            top: "64px", // Adjust the top position to align like the image
          },
        },
      },
    },
    palette: {
      info: {
        main: "#e3f2fd", // Light blue background color
      },
    },
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box
          sx={{
            height: "80vh",
            width: "100%",
            display: "flex",
            marginTop: "50px",
            flexDirection: "column",
            backdropFilter: "blur(10px)" 
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            style={{
              color: "black",
              alignSelf: "flex-start",
              marginBottom: "20px",
              marginTop: "150px",
              fontWeight: "bold",
            }}
          >
            Invoice
          </Typography>
          {isAdmin && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <Autocomplete
                value={selectedEmail}
                onChange={(event, newValue) => {
                  setSelectedEmail(newValue);
                  if (!newValue) {
                    setInvoices([]); // Clear the invoices if the selection is cleared
                  }
                }}
                options={subscribers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Subscriber"
                    variant="outlined"
                    sx={{ marginRight: "10px", minWidth: "600px" }}
                  />
                )}
                sx={{ marginRight: "10px", minWidth: "200px" }}
              />

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#e9a132",
                  color: "white",
                  minWidth: "140px",
                  // marginLeft:"300px",
                  borderRadius: "20px",
                  "&:hover": {
                    backgroundColor: "#b3741e", // Adjust this color to match the hover color of the "Add Subscriber" button
                  },
                }}
                onClick={() => fetchInvoices(selectedEmail)}
                disabled={!selectedEmail}
              >
                Go
              </Button>
            </Box>
          )}
          {isAdmin && (
            <DataGrid
              rows={invoices}
              columns={columns}
              getRowId={(row) => row.invoiceId}
              pageSize={5}
              sx={{ color: "black", border: "1px solid #e9a132" }}
            />
          )}
        </Box>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // Center the snackbar
      >
        <Alert onClose={handleSnackbarClose} severity="info">
          This subscriber does not have any transaction.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default InvoiceAdmin;
